import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

// ─── Native session mirror ───────────────────────────────────────────────────
// On the native shell (iOS/Android via Capacitor), the WebView's localStorage
// lives inside WKWebView/Chromium's own data store, which iOS in particular can
// evict under storage pressure or after the app has been backgrounded a long
// time - wiping accessToken/refreshToken/user and silently logging the rider
// out on next open even though their 365-day token was still perfectly valid.
// Preferences is backed by UserDefaults (iOS) / SharedPreferences (Android),
// which the OS does not evict the same way, so we mirror the session there on
// every login/refresh and rehydrate localStorage from it at startup if
// localStorage came back empty. No-op on web (Preferences just proxies to
// localStorage there, so there'd be nothing to rehydrate from).
const KEYS = ['accessToken', 'refreshToken', 'user', 'loginAt'] as const;

export async function persistSessionNative(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await Promise.all(
      KEYS.map((key) => {
        const value = localStorage.getItem(key);
        return value != null ? Preferences.set({ key, value }) : Preferences.remove({ key });
      }),
    );
  } catch {}
}

export async function clearSessionNative(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await Promise.all(KEYS.map((key) => Preferences.remove({ key })));
  } catch {}
}

// Call once at app startup, before the first render that might redirect to
// /login based on isAuthenticated(). Only fills gaps - never overwrites a
// session localStorage already has.
export async function hydrateSessionFromNative(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  if (localStorage.getItem('accessToken')) return;
  try {
    const entries = await Promise.all(KEYS.map((key) => Preferences.get({ key })));
    const hasSession = entries.some(({ value }) => value != null);
    if (!hasSession) return;
    entries.forEach(({ value }, i) => {
      if (value != null) localStorage.setItem(KEYS[i], value);
    });
  } catch {}
}
